import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Search, CheckCircle2 } from "lucide-react";

interface Option {
  _id: string;
  name: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedIds,
  onChange,
  placeholder = "Select items...",
  label,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((opt) => opt._id));
    }
  };

  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-full" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-semibold text-slate-700 mb-2.5 flex items-center gap-2">
          {label} {required && <span className="text-rose-500">*</span>}
        </label>
      )}
      <div className="relative">
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-[8px] bg-white border-2 border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all duration-200 hover:border-violet-300 hover:shadow-md cursor-pointer group"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {selectedIds.length > 0 ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-md border border-indigo-200">
                    {selectedIds.length} selected
                  </span>
                  <span className="text-sm text-slate-600 font-medium truncate">
                    {selectedIds.length === 1
                      ? options.find((opt) => opt._id === selectedIds[0])?.name
                      : `${selectedIds.length} files selected`}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-slate-500 font-medium">
                  {placeholder}
                </span>
              )}
            </div>
            <ChevronDown
              className={`h-5 w-5 text-slate-400 flex-shrink-0 transition-all duration-200 group-hover:text-violet-500 ${
                isOpen ? "transform rotate-180" : ""
              }`}
            />
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-white border-2 border-slate-200 rounded-md shadow-2xl overflow-hidden">
            {/* Search Bar */}
            <div className="p-3 border-b border-slate-200 bg-slate-50">
              <div className="relative group">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 group-hover:text-indigo-500 transition-colors" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search files..."
                  className="w-full pl-10 pr-3 py-2.5 text-sm border-2 border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all font-medium"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Select All */}
            <div className="px-3 py-2.5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-indigo-50/30">
              <label className="flex items-center justify-between cursor-pointer hover:bg-white rounded-md px-3 py-2 transition-all duration-200 group">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={
                        selectedIds.length === options.length &&
                        options.length > 0
                      }
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
                    />
                  </div>
                  <span className="text-sm font-bold text-slate-700 group-hover:text-indigo-600 transition-colors">
                    Select All Files
                  </span>
                </div>
                <span className="px-2.5 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-lg">
                  {selectedIds.length} / {options.length}
                </span>
              </label>
            </div>

            {/* Options List */}
            <div className="max-h-64 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                <div className="py-1">
                  {filteredOptions.map((option) => {
                    const isSelected = selectedIds.includes(option._id);
                    return (
                      <label
                        key={option._id}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all duration-150 group ${
                          isSelected
                            ? "bg-indigo-50 hover:bg-indigo-100"
                            : "hover:bg-slate-50"
                        }`}
                      >
                        <div className="relative flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggle(option._id)}
                            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
                          />
                        </div>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              isSelected ? "bg-indigo-500" : "bg-slate-300"
                            }`}
                          />
                          <span
                            className={`text-sm flex-1 truncate transition-colors ${
                              isSelected
                                ? "text-indigo-900 font-bold"
                                : "text-slate-700 font-medium group-hover:text-slate-900"
                            }`}
                          >
                            {option.name}
                          </span>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                        )}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="px-4 py-12 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                    <Search className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500 font-medium">
                    No files found
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Try a different search term
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
